import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classes from "./ContextSync.module.scss";
import { fetchKnowledgeBaseData, syncKnowledgeBaseItems } from 'store';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const KnowledgeBaseSyncBox = React.memo(({ onCancel }) => {

    const { knowledgeBaseData, knowledgeBaseLoading, syncKnowledgeBaseLoading, syncKnowledgeBaseSuccess } = useSelector(state => state.aggregator);
    const dispatch = useDispatch();
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchKnowledgeBaseData());
    }, [dispatch]);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const handleToggle = (file) => () => {
        setSelectedFiles((prev) =>
            prev.includes(file)
                ? prev.filter((f) => f !== file)
                : [...prev, file]
        );
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    useEffect(() => {
        if (knowledgeBaseData) {
            setSelectedFiles(knowledgeBaseData.selected_file_list);
        }
    }, [knowledgeBaseData]);

    const combinedList = useMemo(() => {
        const available = knowledgeBaseData?.available_file_list || [];
        const preselected = knowledgeBaseData?.selected_file_list || [];
        return Array.from(new Set([...available, ...preselected]));
    }, [knowledgeBaseData?.available_file_list, knowledgeBaseData?.selected_file_list]);

    const allSelected =
        combinedList?.length > 0 && selectedFiles.length === combinedList.length;

    const handleToggleSelectAll = () => {
        setSelectedFiles((prev) =>
            prev.length === combinedList.length ? [] : combinedList
        );
    };

    const handleSyncSelected = useCallback(() => {
        const payload = { list_of_KB_files: selectedFiles };
        dispatch(syncKnowledgeBaseItems(payload)).then(() => {
            setSnackbarOpen(true);
            dispatch(fetchKnowledgeBaseData());
        })
            .catch((err) => {
            });
    }, [dispatch, selectedFiles]);

    return (
        <Box className={classes.body}>
            <section className={classes.section}>
                <section className={classes.header}>
                    <Typography className={classes.title}>Knowledge Bas Sync</Typography>
                    <Button onClick={handleToggleSelectAll} variant="contained" className={classes.boxButton}>
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                </section>

                <section className={classes.formSection}>
                    <FormControl component="fieldset" className={classes.formControlSection}>
                        {knowledgeBaseLoading ? (
                            <Box className={classes.loader}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <FormGroup>
                                {combinedList.map((file, index) => (
                                    <FormControlLabel
                                        key={file}
                                        control={
                                            <Checkbox
                                                checked={selectedFiles.includes(file)}
                                                onChange={handleToggle(file)}
                                            />
                                        }
                                        label={file}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </FormControl>
                </section>
            </section>

            <section className={classes.buttonFooter}>
                <Button onClick={onCancel} className={classes.boxButton} variant="outlined" disabled={syncKnowledgeBaseLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    className={classes.boxButton}
                    onClick={handleSyncSelected}
                    startIcon={syncKnowledgeBaseLoading ? <CircularProgress size={20} /> : null}
                >
                    {syncKnowledgeBaseLoading ? 'Syncing...' : 'Sync'}
                </Button>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                        {syncKnowledgeBaseSuccess?.status === "success" && (
                            <Typography color="success.main" sx={{ mt: 1 }}>{syncKnowledgeBaseSuccess.message}</Typography>
                        )}
                        {syncKnowledgeBaseSuccess?.status === "error" && (
                            <Typography color="error.main" sx={{ mt: 1 }}>{syncKnowledgeBaseSuccess.message}</Typography>
                        )}
                    </MuiAlert>
                </Snackbar>
            </section>
        </Box>
    );
});

export default KnowledgeBaseSyncBox;
